import { useEffect, useRef } from "react";

const REFRESH_THROTTLE_MS = 2_000;

/**
 * Invoke `refresh` when the workspace revision moves, trailing-throttled.
 *
 * Revisions come from workspace events on the scoped thread stream and fresh
 * reconnect snapshots. Trailing throttling coalesces bursts without losing the
 * final write; ordinary chat tokens do not refresh the file surface.
 */
export function useWorkspaceRevisionRefresh(
  revision: string | undefined,
  refresh: () => void,
): void {
  // A mounted query may reuse an atom cached before the panel was closed.
  // Validate it against the current revision on mount as well as on changes.
  const lastRevision = useRef<string | undefined>(undefined);
  const lastRunAt = useRef(0);
  const trailing = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (revision === lastRevision.current) return;
    lastRevision.current = revision;

    const run = () => {
      lastRunAt.current = Date.now();
      refreshRef.current();
    };
    const elapsed = Date.now() - lastRunAt.current;
    if (trailing.current !== null) clearTimeout(trailing.current);
    if (elapsed >= REFRESH_THROTTLE_MS) {
      run();
      return;
    }
    trailing.current = setTimeout(run, REFRESH_THROTTLE_MS - elapsed);
  }, [revision]);

  useEffect(
    () => () => {
      if (trailing.current !== null) clearTimeout(trailing.current);
    },
    [],
  );
}
